// apps/vendor/src/lib/ui/statusPill.ts
//
// Shared status pill / badge classes — dark-mode aware.
//
// Pre-existing problem (reported 2026-05-28): vendor surfaces used raw
// Tailwind colour pairs like `bg-green-100 text-green-800` which look fine
// on the light theme but become unreadable when the dark `.dark` class
// flips body bg to `#0b0d10` (globals.css). Centralising here forces every
// surface to pick from the same dark-aware palette.
//
// Pattern: light = soft tint (X-100/X-50) with high-contrast dark text
// (X-800/X-900). Dark = translucent dark tint (X-500/20) with light text
// (X-200/X-300) so the badge stays legible on both bg layers.

export type StatusTone =
  | 'neutral'
  | 'info'
  | 'progress'
  | 'review'
  | 'success'
  | 'ship'
  | 'done'
  | 'warning'
  | 'danger';

export const STATUS_PILL_CLASS: Record<StatusTone, string> = {
  neutral:
    'bg-pw-surface text-pw-text',
  info:
    'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200',
  progress:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-200',
  review:
    'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-200',
  success:
    'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200',
  ship:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200',
  done:
    'bg-pw-surface text-pw-text',
  warning:
    'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-200',
  danger:
    'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200',
};

// Stage / column container tint (used by Print Queue lane backgrounds).
// Lighter saturation than pills + visible border in both themes.
export const STAGE_CONTAINER_CLASS: Record<
  'review' | 'success' | 'ship',
  string
> = {
  review:
    'border-purple-300 bg-purple-50 dark:border-purple-800/60 dark:bg-purple-900/20',
  success:
    'border-green-300 bg-green-50 dark:border-green-800/60 dark:bg-green-900/20',
  ship:
    'border-emerald-300 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-900/20',
};

// Map common item lifecycle status string → tone. Centralises the
// "what colour for which status" decision so all tables agree.
export function lifecycleToTone(
  status: string | null | undefined,
): StatusTone {
  switch (status) {
    case 'assigned':
    case 'accepted':
      return 'info';
    case 'in_production':
      return 'progress';
    case 'quality_check':
      return 'review';
    case 'ready_to_ship':
      return 'success';
    case 'shipped':
      return 'ship';
    case 'completed':
      return 'done';
    case 'cancelled':
    case 'rejected':
      return 'danger';
    case 'pending_reassignment':
    case 'on_hold':
      return 'warning';
    case 'pending':
    case 'superseded':
    default:
      return 'neutral';
  }
}

// Blueprint status (used by analytics top-products column).
export function blueprintStatusToTone(
  status: string | null | undefined,
): StatusTone {
  const s = (status ?? '').toLowerCase();
  if (s === 'active') return 'success';
  if (s === 'draft') return 'progress';
  if (s === 'inactive') return 'neutral';
  if (s === 'pendingapproval') return 'review';
  return 'neutral';
}

// Inline notice / alert card (border + tinted bg + readable text). Heavier
// than a pill — used for inline form errors, success confirmations, info
// callouts. Five tones with paired dark variants.
export type NoticeTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export const NOTICE_CLASS: Record<NoticeTone, string> = {
  info:
    'border border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-700/60 dark:bg-blue-900/20 dark:text-blue-200',
  success:
    'border border-green-200 bg-green-50 text-green-800 dark:border-green-700/60 dark:bg-green-900/20 dark:text-green-200',
  warning:
    'border border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-700/60 dark:bg-orange-900/20 dark:text-orange-200',
  danger:
    'border border-red-200 bg-red-50 text-red-700 dark:border-red-700/60 dark:bg-red-900/20 dark:text-red-200',
  neutral:
    'border border-pw-border bg-pw-surface text-pw-text-muted',
};

// Compact notice (same tones, no border — for chips inside table rows).
export const NOTICE_FLAT_CLASS: Record<NoticeTone, string> = {
  info:
    'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
  success:
    'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200',
  warning:
    'bg-orange-50 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200',
  danger:
    'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200',
  neutral:
    'bg-pw-surface text-pw-text-muted',
};
